/**
 * Programmatic SEO for converter tools (currency-converter, unit-converter).
 * Generates dynamic meta tags, content, FAQs, and schema from slug (e.g. usd-to-inr, cm-to-inch).
 */

const BASE_URL = 'https://focuzon.com';

export type ProgrammaticToolId = 'currency-converter' | 'unit-converter';

/** Currency code → full display name */
export const CURRENCY_NAMES: Record<string, string> = {
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  INR: 'Indian Rupee',
  JPY: 'Japanese Yen',
  AUD: 'Australian Dollar',
  CAD: 'Canadian Dollar',
  CHF: 'Swiss Franc',
};

/** Unit slug (e.g. cm, inch) → display name */
const UNIT_DISPLAY_NAMES: Record<string, string> = {
  km: 'kilometers',
  m: 'meters',
  cm: 'centimeters',
  mm: 'millimeters',
  mile: 'miles',
  yard: 'yards',
  foot: 'feet',
  inch: 'inches',
  kg: 'kilograms',
  g: 'grams',
  lb: 'pounds',
  oz: 'ounces',
  '°c': 'Celsius',
  c: 'Celsius',
  '°f': 'Fahrenheit',
  f: 'Fahrenheit',
  k: 'Kelvin',
  l: 'liters',
  ml: 'milliliters',
  'us-gal': 'US gallons',
  'us-fl-oz': 'US fluid ounces',
  'uk-gal': 'UK gallons',
  'uk-fl-oz': 'UK fluid ounces',
};

/** Major currency pairs for sitemap and related links. Order: high-volume first. */
export const MAJOR_CURRENCY_PAIRS = [
  'usd-to-inr',
  'usd-to-eur',
  'usd-to-gbp',
  'eur-to-usd',
  'eur-to-inr',
  'eur-to-gbp',
  'gbp-to-usd',
  'gbp-to-eur',
  'gbp-to-inr',
  'inr-to-usd',
  'inr-to-eur',
  'inr-to-gbp',
  'jpy-to-usd',
  'aud-to-usd',
  'cad-to-usd',
  'chf-to-usd',
] as const;

/** Major unit pairs for sitemap and related links */
export const MAJOR_UNIT_PAIRS = [
  'cm-to-inch',
  'inch-to-cm',
  'km-to-mile',
  'mile-to-km',
  'kg-to-lb',
  'lb-to-kg',
  'm-to-foot',
  'foot-to-m',
  'c-to-f',
  'f-to-c',
  'l-to-us-gal',
  'us-gal-to-l',
] as const;

const CURRENCY_CODES = Object.keys(CURRENCY_NAMES);

/** Parse currency slug e.g. "usd-to-inr" → { from, to } */
export function parseCurrencySlug(slug: string | undefined): { from: string; to: string } | null {
  if (!slug) return null;
  const match = slug.toLowerCase().match(/^([a-z]{3})-to-([a-z]{3})$/);
  if (!match) return null;
  const fromC = match[1].toUpperCase();
  const toC = match[2].toUpperCase();
  if (CURRENCY_CODES.includes(fromC) && CURRENCY_CODES.includes(toC)) return { from: fromC, to: toC };
  return null;
}

/** Unit slug normalization: "°C" → "c", "US gal" → "us-gal" */
function unitToSlug(u: string): string {
  return u.replace(/°/g, '').toLowerCase().replace(/\s+/g, '-');
}

/** Unit config for parsing - must match UnitConverterTool */
const UNIT_CATEGORIES = [
  { id: 'length', units: ['km', 'm', 'cm', 'mm', 'mile', 'yard', 'foot', 'inch'] },
  { id: 'weight', units: ['kg', 'g', 'lb', 'oz'] },
  { id: 'temp', units: ['°C', '°F', 'K'] },
  { id: 'volume', units: ['L', 'mL', 'US gal', 'US fl oz', 'UK gal', 'UK fl oz'] },
];

/** Parse unit slug e.g. "cm-to-inch" → { fromName, toName, categoryId } */
export function parseUnitSlug(
  slug: string | undefined
): { fromName: string; toName: string; fromSlug: string; toSlug: string; categoryId: string } | null {
  if (!slug) return null;
  const parts = slug.toLowerCase().split('-to-');
  if (parts.length !== 2) return null;
  const [fromPart, toPart] = parts;
  for (const cat of UNIT_CATEGORIES) {
    const fromIdx = cat.units.findIndex((u) => unitToSlug(u) === fromPart);
    const toIdx = cat.units.findIndex((u) => unitToSlug(u) === toPart);
    if (fromIdx !== -1 && toIdx !== -1) {
      const fromU = cat.units[fromIdx];
      const toU = cat.units[toIdx];
      return {
        fromName: UNIT_DISPLAY_NAMES[unitToSlug(fromU)] ?? fromU,
        toName: UNIT_DISPLAY_NAMES[unitToSlug(toU)] ?? toU,
        fromSlug: unitToSlug(fromU),
        toSlug: unitToSlug(toU),
        categoryId: cat.id,
      };
    }
  }
  return null;
}

export interface ProgrammaticFAQ {
  question: string;
  answer: string;
}

export interface ProgrammaticSEOConfig {
  title: string;
  metaDescription: string;
  ogTitle: string;
  ogDescription: string;
  h1: string;
  h2Sections: { heading: string; content: string }[];
  faqs: ProgrammaticFAQ[];
  relatedLinks: { label: string; path: string }[];
}

function getCurrencyDisplayName(code: string): string {
  return CURRENCY_NAMES[code] ?? code;
}

/** Generate programmatic SEO for currency-converter slug */
function getCurrencyProgrammaticSEO(slug: string): ProgrammaticSEOConfig | null {
  const parsed = parseCurrencySlug(slug);
  if (!parsed) return null;

  const fromName = getCurrencyDisplayName(parsed.from);
  const toName = getCurrencyDisplayName(parsed.to);

  return {
    title: `${parsed.from} to ${parsed.to} Converter | Convert ${fromName} to ${toName} | Focuzon`,
    metaDescription: `Convert ${parsed.from} to ${parsed.to} instantly using our free currency converter. Get live exchange rates and accurate ${fromName} to ${toName} conversions.`,
    ogTitle: `${parsed.from} to ${parsed.to} Converter | Focuzon`,
    ogDescription: `Convert ${fromName} to ${toName} with live rates. Free currency converter.`,
    h1: `Convert ${parsed.from} to ${parsed.to}`,
    h2Sections: [
      {
        heading: `${parsed.from} to ${parsed.to} Exchange Rate`,
        content: `The ${parsed.from} to ${parsed.to} exchange rate shows how much one ${fromName} is worth in ${toName}. Exchange rates fluctuate based on market conditions. Our free converter uses up-to-date rates so you get accurate ${parsed.from} to ${parsed.to} conversions for travel, business, or personal use.`,
      },
      {
        heading: `How to Convert ${parsed.from} to ${parsed.to}`,
        content: `Enter the amount in ${parsed.from} in the converter above. The tool will instantly show the equivalent value in ${parsed.to} using the latest exchange rate. You can change the amount at any time. Rates update automatically. No signup required.`,
      },
      {
        heading: 'Why Use Online Currency Converters',
        content: `Online currency converters like ours give you instant ${parsed.from} to ${parsed.to} results without visiting a bank or exchange. They are free, fast, and use live or recent rates. Use them for travel planning, international payments, or quick reference.`,
      },
    ],
    faqs: [
      {
        question: `How much is 1 ${parsed.from} in ${parsed.to}?`,
        answer: `The value of 1 ${parsed.from} in ${parsed.to} changes daily based on exchange rates. Use the converter above to see the current rate. Enter 1 in the amount field to get the exact conversion for today. Exchange rates fluctuate throughout the day, so check the date shown with the rate. For large transfers, verify with your bank. Our tool uses live or recent rates from trusted sources for accuracy.`,
      },
      {
        question: `Is the ${parsed.from} to ${parsed.to} rate updated in real time?`,
        answer: `Our currency converter uses current or recent exchange rates from reliable sources. Rates are updated regularly so you get accurate ${parsed.from} to ${parsed.to} conversions for your needs. The tool reflects the latest available data. Check the date shown with the rate. For large transfers or business use, verify with your bank. Free to use with no signup. Works on desktop and mobile.`,
      },
      {
        question: `How do I convert ${parsed.from} to ${parsed.to}?`,
        answer: `Enter the amount in ${parsed.from} in the converter. The tool shows the equivalent in ${parsed.to} automatically. You can swap currencies or change the amount at any time. No registration or signup required. Use the from and to dropdowns to select currencies. The result updates as you type. Free for personal and business use. Works on desktop and mobile.`,
      },
      {
        question: `Is this ${parsed.from} to ${parsed.to} converter free?`,
        answer: `Yes. Our currency converter is free to use with no signup or limits. Convert ${parsed.from} to ${parsed.to} and other major currency pairs anytime. No credit card or account required. The tool works in your browser. Supports USD, EUR, GBP, INR, JPY, AUD, CAD, and CHF. Use as often as you need for travel, payments, or pricing. Focuzon offers it with other free productivity tools.`,
      },
      {
        question: `Which currencies can I convert with this tool?`,
        answer: `You can convert ${parsed.from} to ${parsed.to} and between USD, EUR, GBP, INR, JPY, AUD, CAD, and CHF. Use the dropdowns to change the currency pair. These cover most travel and business needs. The tool shows today's rate and the converted amount. Direct pairs like USD to INR or EUR to GBP work. Enter any amount and see the result instantly. No signup required.`,
      },
    ],
    relatedLinks: MAJOR_CURRENCY_PAIRS.filter((p) => p !== slug)
      .slice(0, 6)
      .map((p) => {
        const m = p.match(/^([a-z]{3})-to-([a-z]{3})$/);
        const label = m ? `${m[1].toUpperCase()} to ${m[2].toUpperCase()}` : p;
        return { label, path: `/tools/currency-converter/${p}` };
      }),
  };
}

/** Generate programmatic SEO for unit-converter slug */
function getUnitProgrammaticSEO(slug: string): ProgrammaticSEOConfig | null {
  const parsed = parseUnitSlug(slug);
  if (!parsed) return null;

  const fromName = parsed.fromName;
  const toName = parsed.toName;
  const shortFrom = parsed.fromSlug.replace(/-/g, ' ');
  const shortTo = parsed.toSlug.replace(/-/g, ' ');

  return {
    title: `${shortFrom} to ${shortTo} Converter | Convert ${fromName} to ${toName} | Focuzon`,
    metaDescription: `Convert ${fromName} to ${toName} instantly with our free unit converter. Accurate ${fromName} to ${toName} conversions for length, weight, temperature, and volume. No signup required.`,
    ogTitle: `${fromName} to ${toName} Converter | Focuzon`,
    ogDescription: `Convert ${fromName} to ${toName}. Free unit converter.`,
    h1: `Convert ${fromName} to ${toName}`,
    h2Sections: [
      {
        heading: `${fromName} to ${toName} Conversion`,
        content: `Converting ${fromName} to ${toName} is easy with our free converter. Enter a value and get the result instantly. The tool supports length, weight, temperature, and volume conversions with accurate conversion factors.`,
      },
      {
        heading: `How to Convert ${fromName} to ${toName}`,
        content: `Enter the value you want to convert in the box above. Select ${fromName} as the "from" unit and ${toName} as the "to" unit. The converter shows the result immediately. You can change the value or units anytime.`,
      },
      {
        heading: 'Why Use Online Unit Converters',
        content: `Online unit converters give you instant results without manual math. They are free, accurate, and work for recipes, travel, DIY, or work. Convert ${fromName} to ${toName} and other common units in seconds.`,
      },
    ],
    faqs: [
      {
        question: `How do I convert ${fromName} to ${toName}?`,
        answer: `Enter your value in the converter and select ${fromName} as the source unit and ${toName} as the target unit. The result appears instantly. No signup or download required. Change the value or units anytime to see updated results. Useful for recipes, travel, DIY, or work. The tool uses accurate conversion factors. Free to use with no account. Works on desktop and mobile.`,
      },
      {
        question: `Is the ${fromName} to ${toName} converter free?`,
        answer: `Yes. Our unit converter is free to use with no registration. Convert ${fromName} to ${toName} and many other unit pairs for length, weight, temperature, and volume. No credit card or account required. Use as often as you need. The tool works in your browser. Supports cm, inch, km, mile, kg, lb, Celsius, Fahrenheit, liters, gallons, and more. Free for personal and business use.`,
      },
      {
        question: `What units can I convert with this tool?`,
        answer: `You can convert ${fromName} to ${toName} and between km, m, cm, inch, mile, kg, lb, Celsius, Fahrenheit, liters, gallons, and more. Choose the category (length, weight, temperature, volume) and units from the dropdowns. Each category has common units. Use direct URL shortcuts for pairs like cm-to-inch or kg-to-lb. Results update as you type. No signup required. Free for all use cases.`,
      },
      {
        question: `Is the ${fromName} to ${toName} conversion accurate?`,
        answer: `Yes. We use standard conversion factors for ${fromName} to ${toName}. Results are accurate for everyday use in cooking, travel, or work. Conversion formulas follow international standards. The tool handles length, weight, temperature, and volume. No rounding errors for typical values. Free to use with no signup. Useful for recipes, DIY projects, travel planning, or professional calculations.`,
      },
    ],
    relatedLinks: MAJOR_UNIT_PAIRS.filter((p) => p !== slug)
      .slice(0, 6)
      .map((p) => {
        const parts = p.split('-to-');
        const label = parts.length === 2 ? `${parts[0]} to ${parts[1]}` : p;
        return { label, path: `/tools/unit-converter/${p}` };
      }),
  };
}

/** Get programmatic SEO config for a tool + slug. Returns null if tool or slug is invalid. */
export function getProgrammaticSEO(
  toolId: ProgrammaticToolId,
  slug: string
): ProgrammaticSEOConfig | null {
  if (toolId === 'currency-converter') return getCurrencyProgrammaticSEO(slug);
  if (toolId === 'unit-converter') return getUnitProgrammaticSEO(slug);
  return null;
}

/** Check if a tool supports programmatic SEO */
export function isProgrammaticTool(toolId: string): toolId is ProgrammaticToolId {
  return toolId === 'currency-converter' || toolId === 'unit-converter';
}

/** Validate slug for programmatic SEO - returns true if we have valid parsed data */
export function isValidProgrammaticSlug(
  toolId: ProgrammaticToolId,
  slug: string
): boolean {
  if (toolId === 'currency-converter') return parseCurrencySlug(slug) !== null;
  if (toolId === 'unit-converter') return parseUnitSlug(slug) !== null;
  return false;
}

/** FAQ schema for programmatic pages */
export function getProgrammaticFAQSchema(faqs: ProgrammaticFAQ[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
}

/** SoftwareApplication schema for programmatic converter pages */
export function getProgrammaticSoftwareAppSchema(
  toolId: ProgrammaticToolId,
  h1: string
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Focuzon Tools',
    applicationCategory: 'UtilityApplication',
    description: `Free ${h1} tool. Convert currencies and units instantly.`,
    url: `${BASE_URL}/tools/${toolId}`,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  };
}
